import React, { createContext, useCallback, useContext, useState, useEffect, type ReactNode } from 'react';

export type ColorTheme = 'default' | 'blue' | 'indigo' | 'purple' | 'pink' | 'red' | 'orange' | 'yellow' | 'green' | 'teal' | 'cyan' | 'chocolate' | 'brown' | 'grey' | 'black' | 'pista';
export type BackgroundGradient = 'bg-default' | 'bg-white' | 'bg-r-gradient' | 'bg-gradient-1' | 'bg-gradient-2' | 'bg-gradient-3' | 'bg-gradient-4' | 'bg-gradient-5' | 'bg-gradient-6' | 'bg-gradient-7' | 'bg-gradient-8' | 'bg-gradient-9' | 'bg-gradient-10';
export type HeaderLayout = 'default' | 'standard' | 'boxed';
export type HeaderStyle = 'transparent' | 'white' | 'bg' | 'theme' | 'accent';
export type SidebarLayout = 'default' | 'standard' | 'boxed' | 'iconic';
export type SidebarStyle = 'transparent' | 'white' | 'bg' | 'theme' | 'accent';
export type Direction = 'ltr' | 'rtl';

interface ThemeSettings {
  colorTheme: ColorTheme;
  backgroundGradient: BackgroundGradient;
  darkMode: boolean;
  headerLayout: HeaderLayout;
  headerStyle: HeaderStyle;
  sidebarLayout: SidebarLayout;
  sidebarStyle: SidebarStyle;
  direction: Direction;
  /**
   * Altura do nome CASAA ao lado do símbolo redondo, em px. Nasceu como
   * experimento na bancada `/dev/header` (fatia 2, 26/08/2026), onde era CSS
   * injetado ao vivo e morria ao sair da rota. Virou escolha de verdade em
   * 28/08 — ele viu na bancada e quis na tela: *"aquilo ali que eu achei
   * interessante"*.
   *
   * Vai como número, e não como classe, porque o efeito é uma medida contínua:
   * quem escolhe compara olhando a barra em cima, e 24 ou 26 é um passo, não
   * uma categoria. O valor sai daqui direto para a variável
   * `--casaa-marca-altura`, lida em `global_styles.scss`.
   */
  tamanhoMarca: number;
}

interface ThemeContextType extends ThemeSettings {
  setColorTheme: (theme: ColorTheme) => void;
  setBackgroundGradient: (gradient: BackgroundGradient) => void;
  setDarkMode: (enabled: boolean) => void;
  setHeaderLayout: (layout: HeaderLayout) => void;
  setHeaderStyle: (style: HeaderStyle) => void;
  setSidebarLayout: (layout: SidebarLayout) => void;
  setSidebarStyle: (style: SidebarStyle) => void;
  setDirection: (dir: Direction) => void;
  setTamanhoMarca: (px: number) => void;

  /* ─── Experimentar sem medo (28/08/2026) ────────────────────────────────
   * Ele pediu: *"a gente experimenta, mexe, mexe, mexe, no final você tem que
   * salvar"* — e um jeito de voltar, porque *"o pessoal da loja não vai ter
   * desembaraço para mexer nisso"*.
   *
   * O app SEMPRE guardou tudo na hora, e é por isso que faltava o botão: quem
   * mexia já tinha mexido, e não havia para onde voltar. Agora são DUAS
   * memórias — a que está na tela e a última confirmada:
   *
   *   `adminuiux-theme-settings` → o que está aplicado AGORA (o rascunho)
   *   `casaa-aparencia-salva`    → a última combinação confirmada
   *
   * `salvar` iguala as duas; `desfazer` traz a salva de volta; `voltarAoPadrao`
   * traz a cara da casa. `temAlteracaoNaoSalva` é o que acende o botão — sem
   * ele, "Salvar" fica aceso o tempo todo e para de querer dizer alguma coisa.
   */
  salvar: () => void;
  desfazer: () => void;
  /**
   * Sair de onde se estava experimentando DESFAZ o que não foi salvo.
   *
   * Ele apontou o furo em 28/08: *"se eu seleciono o rosa e vou para o Início,
   * ele continua rosa — não faz sentido nenhum"*. Não fazia: o rascunho estava
   * sendo gravado como se fosse decisão, e o botão Salvar não queria dizer
   * nada. Agora quem sai da tela de Aparência, ou fecha o painel da paleta, sem
   * ter salvado, volta ao que estava.
   *
   * É `useCallback` com dependência vazia de propósito: quem o chama o usa como
   * função de limpeza de `useEffect`, e uma identidade nova a cada render faria
   * a limpeza rodar a cada tecla. Ele lê a última salva do `localStorage` na
   * hora — não uma cópia velha —, senão salvar e depois sair desfaria o que
   * acabou de ser salvo.
   */
  descartarRascunho: () => void;
  voltarAoPadrao: () => void;
  temAlteracaoNaoSalva: boolean;
}

/**
 * A CARA DA CASA A — congelada em 24/08/2026, a pedido dele.
 *
 * Estas oito escolhas são o visual que ele ajustou ao longo de meses e **não
 * quer perder**. Elas são independentes entre si — é o que confundia:
 *
 * | O que é | Valor | Muda cor? |
 * |---|---|---|
 * | `colorTheme` | esquema de cores, 1 de 16 | **sim** — troca `theme-1` e `accent-1` |
 * | `darkMode` | claro ou escuro | **sim** — troca fundos e texto |
 * | `backgroundGradient` | o fundo, 1 de 13 | só o fundo, não o esquema |
 * | `headerStyle` / `sidebarStyle` | qual cor JÁ EXISTENTE o cabeçalho e o menu usam | não criam cor |
 * | `headerLayout` / `sidebarLayout` / `direction` | forma, não cor | não |
 *
 * **Trocar o fundo NÃO troca o esquema de cores, e trocar o estilo do menu não
 * troca nem uma coisa nem outra.** São botões diferentes; o tema só chama tudo
 * de "personalização".
 *
 * ⚠️ **Mexer aqui muda a cara do app para todo mundo que nunca personalizou.**
 * Não é preferência de quem está editando o arquivo — é o padrão da casa. A
 * escolha de cada pessoa vive no `localStorage` e ganha desta.
 */
const defaultSettings: ThemeSettings = {
  colorTheme: 'blue',
  backgroundGradient: 'bg-r-gradient',
  darkMode: false,
  headerLayout: 'standard',
  headerStyle: 'transparent',
  sidebarLayout: 'standard',
  sidebarStyle: 'white',
  direction: 'ltr',
  // 26px é o valor que estava cravado no `global_styles.scss` desde 26/08 — o
  // padrão não muda para ninguém ao virar preferência.
  tamanhoMarca: 26,
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'adminuiux-theme-settings';
/** A última combinação CONFIRMADA — é para cá que "Desfazer" volta. */
const STORAGE_SALVA = 'casaa-aparencia-salva';

function lerGuardado(chave: string): ThemeSettings | null {
  const bruto = localStorage.getItem(chave);
  if (!bruto) return null;
  try {
    return { ...defaultSettings, ...JSON.parse(bruto) };
  } catch {
    return null;
  }
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<ThemeSettings>(
    () => lerGuardado(STORAGE_KEY) ?? defaultSettings,
  );

  /**
   * A última combinação confirmada. Quem já usava o app antes de existir botão
   * de salvar não tem esta chave — e para essa pessoa o que estava aplicado JÁ
   * era a escolha dela, então ela nasce igual ao rascunho. Sem isso, o primeiro
   * "Desfazer" jogaria a cara que ela montou ao longo de meses no lixo.
   */
  const [salva, setSalva] = useState<ThemeSettings>(
    () => lerGuardado(STORAGE_SALVA) ?? lerGuardado(STORAGE_KEY) ?? defaultSettings,
  );

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_SALVA)) {
      localStorage.setItem(STORAGE_SALVA, JSON.stringify(salva));
    }
    // só na montagem: é a adoção de quem já tinha tema antes do botão existir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const descartarRascunho = useCallback(() => {
    const confirmada = lerGuardado(STORAGE_SALVA);
    if (confirmada) setSettings(confirmada);
  }, []);

  const salvar = () => {
    localStorage.setItem(STORAGE_SALVA, JSON.stringify(settings));
    setSalva(settings);
  };
  const desfazer = () => setSettings(salva);
  const voltarAoPadrao = () => setSettings(defaultSettings);
  const temAlteracaoNaoSalva = JSON.stringify(settings) !== JSON.stringify(salva);

  // Save to localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  /**
   * A altura da marca vai como VARIÁVEL no `<body>`, não como classe: são seis
   * valores hoje e podem ser outros amanhã, e uma classe por px encheria o CSS
   * de regras que só diferem num número. Quem lê a variável é a regra de
   * `.casaa-wordmark-alta` em `global_styles.scss`, que mantém 26px como
   * reserva para quem nunca escolheu.
   */
  useEffect(() => {
    document.body.style.setProperty('--casaa-marca-altura', `${settings.tamanhoMarca}px`);
  }, [settings.tamanhoMarca]);

  // Apply theme classes to body
  useEffect(() => {
    const body = document.body;

    // Remove all theme classes
    body.classList.remove(
      'theme-blue', 'theme-indigo', 'theme-purple', 'theme-pink', 'theme-red',
      'theme-orange', 'theme-yellow', 'theme-green', 'theme-teal', 'theme-cyan',
      'theme-chocolate', 'theme-brown', 'theme-grey', 'theme-black', 'theme-pista'
    );

    // Add color theme class
    if (settings.colorTheme !== 'default') {
      body.classList.add(`theme-${settings.colorTheme}`);
    }

    // Remove all background gradient classes
    body.classList.remove(
      'bg-default', 'bg-white', 'bg-r-gradient',
      'bg-gradient-1', 'bg-gradient-2', 'bg-gradient-3', 'bg-gradient-4', 'bg-gradient-5',
      'bg-gradient-6', 'bg-gradient-7', 'bg-gradient-8', 'bg-gradient-9', 'bg-gradient-10'
    );

    // Add background gradient class
    body.classList.add(settings.backgroundGradient);

    // Apply dark mode
    document.documentElement.setAttribute('data-bs-theme', settings.darkMode ? 'dark' : 'light');

    // Remove all header layout classes
    body.classList.remove('adminuiux-header-boxed');

    // Add header layout class
    if (settings.headerLayout === 'boxed') {
      body.classList.add('adminuiux-header-boxed');
    }

    // Remove all header style classes
    body.classList.remove(
      'adminuiux-header-transparent',
      'adminuiux-header-fill-white',
      'adminuiux-header-fill-bg',
      'adminuiux-header-fill-theme',
      'adminuiux-header-fill-accent'
    );

    // Add header style class
    if (settings.headerStyle !== 'transparent') {
      body.classList.add(`adminuiux-header-fill-${settings.headerStyle}`);
    }

    // Remove all sidebar layout classes
    body.classList.remove(
      'adminuiux-sidebar-boxed',
      'adminuiux-sidebar-iconic'
    );

    // Add sidebar layout class
    if (settings.sidebarLayout === 'boxed') {
      body.classList.add('adminuiux-sidebar-boxed');
    } else if (settings.sidebarLayout === 'iconic') {
      body.classList.add('adminuiux-sidebar-iconic');
    }

    // Remove all sidebar style classes
    body.classList.remove(
      'adminuiux-sidebar-fill-white',
      'adminuiux-sidebar-fill-bg',
      'adminuiux-sidebar-fill-theme',
      'adminuiux-sidebar-fill-accent'
    );

    // Add sidebar style class
    if (settings.sidebarStyle !== 'transparent') {
      body.classList.add(`adminuiux-sidebar-fill-${settings.sidebarStyle}`);
    }

    // Apply direction
    document.documentElement.setAttribute('dir', settings.direction);
  }, [settings]);

  const setColorTheme = (theme: ColorTheme) => {
    setSettings(prev => ({ ...prev, colorTheme: theme }));
  };

  const setBackgroundGradient = (gradient: BackgroundGradient) => {
    setSettings(prev => ({ ...prev, backgroundGradient: gradient }));
  };

  const setDarkMode = (enabled: boolean) => {
    setSettings(prev => ({ ...prev, darkMode: enabled }));
  };

  const setHeaderLayout = (layout: HeaderLayout) => {
    setSettings(prev => ({ ...prev, headerLayout: layout }));
  };

  const setHeaderStyle = (style: HeaderStyle) => {
    setSettings(prev => ({ ...prev, headerStyle: style }));
  };

  const setSidebarLayout = (layout: SidebarLayout) => {
    setSettings(prev => ({ ...prev, sidebarLayout: layout }));
  };

  const setSidebarStyle = (style: SidebarStyle) => {
    setSettings(prev => ({ ...prev, sidebarStyle: style }));
  };

  const setTamanhoMarca = (px: number) => {
    setSettings((s) => ({ ...s, tamanhoMarca: px }));
  };

  const setDirection = (dir: Direction) => {
    setSettings(prev => ({ ...prev, direction: dir }));
  };

  return (
    <ThemeContext.Provider
      value={{
        ...settings,
        setColorTheme,
        setTamanhoMarca,
        salvar,
        desfazer,
        descartarRascunho,
        voltarAoPadrao,
        temAlteracaoNaoSalva,
        setBackgroundGradient,
        setDarkMode,
        setHeaderLayout,
        setHeaderStyle,
        setSidebarLayout,
        setSidebarStyle,
        setDirection,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
